import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';

import {
  answerBlock
} from 'actions';

import Question from 'components/Questionnaires/Question';
import { QuestionTextField, QuestionDropDownField, QuestionJurisdictionDropDownField } from 'components/Questionnaires/fields';
import { requiredValidator, validateQuestion } from 'components/Questionnaires/validators';

export const QuestionTaxResidenceBlock = ({ block, onAnswer, preQualAnswers }) => {
  const dispatch = useDispatch();
  const { reasonItems, reasonOtherValue } = block.componentProps ?? {};
  const [answer, setAnswer] = useState(block?.answers ?? {});

  const [country, setCountry] = useState({
    id: 'country',
    label: 'questionnaire.taxResidence.country.label',
    component: QuestionJurisdictionDropDownField,
    visible: true,
    validation: block.componentProps?.optional !== true ? {
      conditions: [
        { condition: requiredValidator, message: 'questionnaire.taxResidence.country.errorMessage' }
      ]
    } : null,
  });

  const tin = {
    id: 'tin',
    label: 'questionnaire.taxResidence.tin.label',
    component: QuestionTextField,
    info: block?.componentProps?.info ?? {
      instructions: 'questionnaire.foreignTin.info'
    },
    visible: true,
  };

  const [reason, setReason] = useState({
    id: 'reason',
    label: 'questionnaire.taxResidence.reason.label',
    component: QuestionDropDownField,
    componentProps: {
      items: reasonItems ?? 'questionnaire.taxResidence.reasons',
    },
    otherValue: reasonOtherValue,
    className: 'col-12',
    visible: true,
  });

  const [reasonExplanation, setReasonExplanation] = useState({
    id: 'reasonExplanation',
    label: 'questionnaire.taxResidence.reasonExplanation.label',
    component: QuestionTextField,
    className: 'col-12',
    visible: (bAnswers, qAnswers, pAnswers) => bAnswers?.reason === 'B' || bAnswers?.reason === '3',
  });

  const blockIdentifier = useMemo(() => ((block && block.id) ? `${ block.id }${ block.index !== undefined ? (`.${ block.index }`) : '' }` : undefined), [block.index]);

  const isValid = () => {
    if (block.componentProps?.optional !== true) {
      if (country.answer === undefined) {
        return undefined;
      }
      return country.answer !== '';
    }
    if (reason.answer !== undefined) {
      if (reason.answer === 'B') {
        if (reasonExplanation.answer === undefined) {
          return undefined;
        }
        return reasonExplanation.isValid;
      }
    }
    return true;
  };

  useEffect(() => {
    if (!onAnswer || typeof onAnswer !== 'function') {
      dispatch(answerBlock(block.id, answer, isValid()));
    } else {
      onAnswer(block, answer, isValid());
    }
  }, [answer]);

  useEffect(() => {
    if (isEqual(answer, block.answers ?? {})) {
      return;
    }
    setAnswer(block.answers ?? {});
  }, [block.answers]);

  const reasonExplanationValidator = { validation: {
    conditions: [
      { condition: requiredValidator, message: 'questionnaire.taxResidence.reasonExplanation.errorMessage' }
    ]
  } };

  const onQuestionChange = (q, value) => {
    switch (q.id) {
      case 'country': {
        const newCountry = { ...q, answer: value };
        validateQuestion(newCountry);
        setCountry(newCountry);
        setAnswer({ ...answer, country: value });
        break;
      }
      case 'tin': {
        setAnswer({ ...answer, tin: value });
        break;
      }
      case 'reason': {
        const newReason = { ...q, answer: value };
        const isReasonB = value === 'B';
        if (isReasonB) {
          newReason.className = 'col-6';
        } else {
          newReason.className = 'col-12';
        }
        setReason(newReason);
        const explanationValidator = isReasonB ? reasonExplanationValidator : undefined;
        setReasonExplanation({ ...reasonExplanation, ...explanationValidator, className: isReasonB ? 'col-6' : 'col-12' });
        const newAnswers = { reason: value };
        if (!isReasonB) {
          setReasonExplanation({ ...reasonExplanation, ...{ answer: undefined, isValid: undefined } });
          newAnswers.reasonExplanation = undefined;
        }
        setAnswer({ ...answer, ...newAnswers });
        break;
      }
      case 'reasonExplanation': {
        const newExplanation = { ...q, answer: value };
        validateQuestion(newExplanation);
        setReasonExplanation(newExplanation);
        setAnswer({ ...answer, reasonExplanation: value });
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (block.isForceEdited === true) {
      if (block.componentProps?.optional !== true && answer?.country === undefined) {
        onQuestionChange({ ...country, isForceEdited: true }, '');
      }
      if (answer?.reason === 'B' && !answer?.reasonExplanation) {
        onQuestionChange({ ...reasonExplanation, isForceEdited: true }, '');
      }
    }
  }, [block.isForceEdited]);

  return (<div className="row">
    <Question key={ country.id } blockId={ blockIdentifier } question={ country } { ...country.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ tin.id } blockId={ blockIdentifier } question={ tin } { ...tin.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    <Question key={ reason.id } blockId={ blockIdentifier } question={ reason } { ...reason.componentProps } onChange={ (q, a) => onQuestionChange(q, a) } />
    { reasonExplanation.visible(block?.answers, {}, preQualAnswers) && <Question
      key={ reasonExplanation.id }
      blockId={ blockIdentifier }
      question={ reasonExplanation }
      { ...reasonExplanation.componentProps }
      onChange={ (q, a) => onQuestionChange(q, a) }
    /> }
  </div>);
};

QuestionTaxResidenceBlock.displayName = 'QuestionTaxResidenceBlock';

QuestionTaxResidenceBlock.propTypes = {
  block: PropTypes.shape().isRequired,
  onAnswer: PropTypes.func,
  preQualAnswers: PropTypes.shape()
};

QuestionTaxResidenceBlock.defaultProps = {
  onAnswer: null,
  preQualAnswers: null
};

export default QuestionTaxResidenceBlock;
